import { media } from 'utils/styles';
//
import { COLUMN_GAP, ROW_GAP } from 'utils/constants';
import { SystemStyleObject } from '@telescope/cassini-ui';

interface VoteOptionStylesProps {
  round: number;
  region: number;
  position: number;
  columns: number;
  roundDisplay: number;
  isActive: boolean;
  height: number | undefined;
  isWinner: boolean;
}

export const stylesheet = ({
  round,
  region,
  position,
  columns,
  roundDisplay,
  height,
  isWinner,
}: VoteOptionStylesProps): Record<string, SystemStyleObject> => {
  // Space between vote options grows exponentially by round.
  // Get exp using round (2**2) for round 3, for example.
  const exp = 2 ** (roundDisplay - 1);

  // If round === 1, starting row should be. All the rest should grow exponentially
  const startingRow = roundDisplay === 1 ? 1 : exp;

  // If team number is 1, this is the first team to be displayed on the column. In this case, use starting row
  // If round === 1, row is determined by the team number and round
  // The rest should use the exp value above to get the space between teams on the round column.
  const gridRow =
    position === 1
      ? startingRow
      : roundDisplay === 1
      ? position * (roundDisplay * 2) - roundDisplay
      : startingRow + exp * 2 * (position - 1);

  // If the round number is equal to the amount of columns, round is the final round.
  const isFinalRound = columns === round;

  return {
    voteOption: {
      gridRow: gridRow,
      gridColumn: round,
      position: 'relative',

      ':empty': {
        display: 'none',
      },

      [media.tablet]: {},

      [media.desktop]: {
        gridColumn: region === 1 ? roundDisplay : columns - roundDisplay + 1,
      },

      ...(!isWinner && {
        ':before': {
          content: '""',
          position: 'absolute',
          width: roundDisplay === 1 ? COLUMN_GAP : COLUMN_GAP / 2,
          height: 2,
          borderTop: roundDisplay === 1 ? 'none' : '1px solid var(--cassini-colors-BracketGridVoteOption-border)',
          top: '50%',
          left: 0,
          transform: 'translateX(-100%)',

          [media.desktop]: {
            // Left vs Right
            ...(region === 2 && {
              left: 'unset',
              right: 0,
              transform: 'translateX(100%)',
            }),

            //
            ...(round === 1 && {
              display: 'none',
            }),
          },
        },

        ':after': {
          content: '""',
          position: 'absolute',
          border: '1px solid var(--cassini-colors-BracketGridVoteOption-border)',
          width: COLUMN_GAP / 2,
          height: height ? `${height * exp + ROW_GAP * exp}px` : `calc(150% * ${exp})`,
          right: 0,
          borderLeft: 'none',
          transform: 'translateX(100%)',

          // Top vs Bottom option
          // Final round / region 2 bracket line acts like Bottom on mobile and tablet
          ...(position % 2 === 0 || (isFinalRound && region === 2)
            ? {
                borderTop: 'none',
                bottom: '50%',
              }
            : {
                borderBottom: 'none',
                top: '50%',
              }),

          [media.tablet]: {
            //
          },

          [media.desktop]: {
            // Left vs Right
            ...(region === 1
              ? {
                  right: 0,
                  borderLeft: 'none',
                  borderRight: isFinalRound ? 'none' : '1px solid var(--cassini-colors-BracketGridVoteOption-border)',
                  transform: 'translateX(100%)',
                }
              : {
                  right: 'unset',
                  left: 0,
                  borderLeft: isFinalRound ? 'none' : '1px solid var(--cassini-colors-BracketGridVoteOption-border)',
                  borderRight: 'none',
                  transform: 'translateX(-100%)',
                }),
          },
        },
      }),
    },

    name: {
      margin: 0,
      padding: '5px 0',
      width: '100%',
      textAlign: 'center',
      fontSize: { base: 'clamp(0.75rem, 0.5714285714285714rem + 0.8928571428571428vw, 1rem)', lg: 'xs' },
    },
  };
};
